import { inject, Injectable } from '@angular/core';

import { Timestamp } from '@ts/shared/date-time/util-core';
import { Money, MoneyOperationService } from '@ts/shared/money/util-core';

import { Order, OrderLine } from './order.model';
import { OrderProgress } from './order-progress';
import { PaymentEvent } from './payment-event.model';

export interface OrderReturnsBase {
  quantity: number;
  // When the money went back: the latest refund.
  timestamp: Timestamp | null;
}

export type OrderReturns = Readonly<OrderReturnsBase>;

export interface OrderRefundBase {
  returnedLines: readonly OrderLine[];
  returns: OrderReturns | null;
  // What the refund events gave back (payer.py); null when nothing did.
  refundTotal: Money | null;
  refundTotalNegative: Money | null;
  // What a cancellation put back in the wallet (#11398 D3).
  walletRefund: Money | null;
  // What a return put back in the wallet; a till order refunds at the till.
  returnWalletRefund: Money | null;
  // The total paid less what a return gave back (#11398 D2).
  finalTotal: Money | null;
}

export type OrderRefund = Readonly<OrderRefundBase>;

/** What came back to the customer, from the order's refund events. */
@Injectable({
  providedIn: 'root',
})
export class OrderRefundService {
  private moneyOperationService = inject(MoneyOperationService);

  getOrderRefund(
    order: Order,
    orderLines: readonly OrderLine[],
    paymentEvents: readonly PaymentEvent[],
    orderProgress: OrderProgress,
  ): OrderRefund {
    const refunds = paymentEvents.filter(
      (paymentEvent) => paymentEvent.event_type.name === 'Payment Refunded',
    );
    const refundTotal = this.getRefundTotal(refunds);
    const returnedLines = orderLines.filter(
      (orderLine) => orderLine.quantity_return_cached > 0,
    );
    const returns = this.getReturns(returnedLines, refunds);
    return {
      returnedLines,
      returns,
      refundTotal,
      refundTotalNegative: refundTotal
        ? this.moneyOperationService.moneyMultiply(refundTotal, -1)
        : null,
      walletRefund: orderProgress.stage === 'cancelled' ? refundTotal : null,
      returnWalletRefund:
        returns && orderProgress.flow !== 'store' ? refundTotal : null,
      finalTotal:
        returns && refundTotal
          ? this.moneyOperationService.moneySubtract(
              order.total_price,
              refundTotal,
            )
          : null,
    };
  }

  private getRefundTotal(refunds: readonly PaymentEvent[]): Money | null {
    if (refunds.length === 0) {
      return null;
    }
    return refunds
      .map((refund) => refund.amount)
      .reduce((total, amount) =>
        this.moneyOperationService.moneyAdd(total, amount),
      );
  }

  private getReturns(
    returnedLines: readonly OrderLine[],
    refunds: readonly PaymentEvent[],
  ): OrderReturns | null {
    if (returnedLines.length === 0) {
      return null;
    }
    const refundTimestamps = refunds.map(
      (refund) => refund.date_created_timestamp,
    );
    return {
      quantity: returnedLines.reduce(
        (quantity, orderLine) => quantity + orderLine.quantity_return_cached,
        0,
      ),
      timestamp:
        refundTimestamps.length > 0 ? Math.max(...refundTimestamps) : null,
    };
  }
}
